import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Eye, EyeOff, Landmark } from 'lucide-react';
import { api } from '../../lib/api';
import { Button, Card, Field, Input, Pill } from '../../components/ui';

interface GatewayView {
  configured: boolean;
  enabled: boolean;
  currencies: string[];
  webhookUrl: string;
}
export interface BillingSettingsView {
  ratePerOltMinor: number;
  currency: string;
  rates: Partial<Record<'KES' | 'USD', number>>;
  fx: Partial<Record<'KES' | 'USD', number>>;
  currencies: string[];
  mpesa: GatewayView & {
    consumerKey: string;
    consumerSecret: string;
    shortcode: string;
    passkey: string;
    environment: string;
    /** The URLs to register against the shortcode for paybill payments. */
    c2bConfirmationUrl: string;
    c2bValidationUrl: string;
    /** Where Safaricom posts the verdict on a Transaction Status query. */
    c2bResultUrl: string;
    /**
     * Where Safaricom posts a QUEUE TIMEOUT. A different address from the
     * verdict on purpose: the two arrive in the identical envelope, so which
     * URL Daraja chose is the only thing that says "we could not answer"
     * rather than "this payment is not real".
     */
    c2bTimeoutUrl: string;
    /** Whether Safaricom can be asked at all. FALSE means everything is held. */
    c2bVerifiable: boolean;
    /** The Daraja API operator name. Not a secret — useless without the credential. */
    initiatorName: string;
    /** Whether a security credential is stored. The value itself never leaves the server. */
    securityCredential: boolean;
  };
  stripe: GatewayView & { secretKey: string; webhookSecret: string; webhook: boolean };
  paystack: GatewayView & { secretKey: string };
}

/**
 * Payment gateway credentials — each gateway its own section (the operator's
 * call), with an enable/disable toggle that cuts a gateway off WITHOUT
 * deleting its keys, and the webhook URL to paste into that gateway's
 * dashboard. Per-transaction callback/return URLs are sent automatically
 * with every payment — the standing webhook endpoint is the only half the
 * dashboards must be told about (the hybrid the operator asked about).
 *
 * Saved values come back INTO their fields on load (operator's call: a saved
 * key must look saved, refresh included). Secrets render masked; Show/Mask
 * flips visibility locally — the values are already on this superadmin-only
 * screen, so a password gate on top would be theater, and there isn't one.
 */
export function GatewayCredentialsCard() {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['billing', 'settings'],
    queryFn: () => api.get<BillingSettingsView>('/billing/settings'),
  });
  const [form, setForm] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unmasked, setUnmasked] = useState(false);
  const [savingGateway, setSavingGateway] = useState<string | null>(null);

  // Seed the fields from the stored values ONCE per page visit — later
  // refetches (after saves) must not clobber what is being typed.
  const seeded = useRef(false);
  useEffect(() => {
    if (!settings.data || seeded.current) return;
    seeded.current = true;
    const d = settings.data;
    setForm({
      mpesaConsumerKey: d.mpesa.consumerKey,
      mpesaConsumerSecret: d.mpesa.consumerSecret,
      mpesaShortcode: d.mpesa.shortcode,
      mpesaPasskey: d.mpesa.passkey,
      mpesaEnvironment: d.mpesa.environment,
      stripeSecretKey: d.stripe.secretKey,
      stripeWebhookSecret: d.stripe.webhookSecret,
      paystackSecretKey: d.paystack.secretKey,
    });
  }, [settings.data]);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put('/billing/settings', body),
    onSuccess: async () => {
      setError(null);
      setNotice('Saved.');
      // The fields KEEP what was typed (operator's call) — a form that blanks
      // itself on save reads as "your keys vanished". Secrets stay masked.
      setTimeout(() => setNotice(null), 4000);
      await queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save'),
  });

  const toggle = useMutation({
    mutationFn: (input: { key: 'mpesaEnabled' | 'stripeEnabled' | 'paystackEnabled'; value: boolean }) =>
      api.put('/billing/settings', { [input.key]: input.value }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing'] }),
  });

  const s = settings.data;
  if (!s) return null;
  const set = (k: string) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });
  /**
   * Each gateway saves ONLY its own fields (found live by the operator: the
   * browser autofilled a login into the M-PESA pair, and a global save then
   * wrote it over real credentials while saving a DIFFERENT gateway). A
   * section's save cannot touch another section's keys, whatever a password
   * manager stuffed into them.
   */
  const GATEWAY_FIELDS = {
    mpesa: ['mpesaConsumerKey', 'mpesaConsumerSecret', 'mpesaShortcode', 'mpesaPasskey', 'mpesaEnvironment'],
    stripe: ['stripeSecretKey', 'stripeWebhookSecret'],
    paystack: ['paystackSecretKey'],
  } as const;
  const saveGateway = (gateway: keyof typeof GATEWAY_FIELDS) => {
    const body: Record<string, unknown> = {};
    for (const k of GATEWAY_FIELDS[gateway]) {
      const v = form[k];
      if (v !== undefined && v !== '') body[k] = v;
    }
    setSavingGateway(gateway);
    save.mutate(body, { onSettled: () => setSavingGateway(null) });
  };

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Landmark className="size-4 text-muted" />
          Payment gateway credentials
        </span>
      }
      action={
        <Button
          size="sm"
          variant="ghost"
          icon={unmasked ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
          onClick={() => setUnmasked((v) => !v)}
        >
          {unmasked ? 'Mask values' : 'Show values'}
        </Button>
      }
    >
      <p className="border-b border-line px-5 py-3 text-sm text-muted">
        These let SimpleACS charge out through the gateways. Saved values stay in their fields —
        secrets masked until Show. Per-payment callback URLs are sent automatically; only the{' '}
        <b>webhook URL</b> shown per gateway needs pasting into its dashboard.
      </p>

      <GatewaySection
        name="M-PESA (Daraja)"
        view={s.mpesa}
        note="KES only — the gateway's rule."
        onToggle={(value) => toggle.mutate({ key: 'mpesaEnabled', value })}
        toggling={toggle.isPending && toggle.variables?.key === 'mpesaEnabled'}
        onSave={() => saveGateway('mpesa')}
        saving={save.isPending && savingGateway === 'mpesa'}
      >
        <CredField label="Consumer key" k="mpesaConsumerKey" form={form} set={set} unmasked={unmasked} />
        <CredField label="Consumer secret" k="mpesaConsumerSecret" form={form} set={set} secret unmasked={unmasked} />
        <CredField label="Passkey" k="mpesaPasskey" form={form} set={set} secret unmasked={unmasked} />
        <div className="grid gap-4 sm:grid-cols-2">
          <CredField label="Shortcode" k="mpesaShortcode" form={form} set={set} placeholder={s.mpesa.shortcode || '174379'} unmasked={unmasked} />
          <Field label="Environment">
            <select className="input" value={form.mpesaEnvironment ?? s.mpesa.environment} onChange={set('mpesaEnvironment')}>
              <option value="sandbox">sandbox</option>
              <option value="production">production</option>
            </select>
          </Field>
        </div>
        <C2bBlock view={s.mpesa} />
      </GatewaySection>

      <GatewaySection
        name="Stripe"
        view={s.stripe}
        note="USD and KES."
        onToggle={(value) => toggle.mutate({ key: 'stripeEnabled', value })}
        toggling={toggle.isPending && toggle.variables?.key === 'stripeEnabled'}
        onSave={() => saveGateway('stripe')}
        saving={save.isPending && savingGateway === 'stripe'}
      >
        <CredField label="Secret key" k="stripeSecretKey" form={form} set={set} secret unmasked={unmasked} />
        <CredField label="Webhook signing secret (optional — the return redirect verifies too)" k="stripeWebhookSecret" form={form} set={set} secret unmasked={unmasked} />
      </GatewaySection>

      <GatewaySection
        name="Paystack"
        view={s.paystack}
        note="USD and KES. Its own credentials — nothing shared with Stripe."
        onToggle={(value) => toggle.mutate({ key: 'paystackEnabled', value })}
        toggling={toggle.isPending && toggle.variables?.key === 'paystackEnabled'}
        onSave={() => saveGateway('paystack')}
        saving={save.isPending && savingGateway === 'paystack'}
      >
        <CredField label="Secret key" k="paystackSecretKey" form={form} set={set} secret unmasked={unmasked} />
      </GatewaySection>

      {error && <p className="px-5 py-3 text-sm text-danger">{error}</p>}
      {notice && <p className="px-5 py-3 text-sm text-success">{notice}</p>}
    </Card>
  );
}

/**
 * The paybill half of M-PESA: the URLs to register, and the one credential
 * that decides whether a paybill payment can ever be credited without a person
 * looking at it.
 *
 * It used to be two settings — an allowlist of Safaricom's source addresses
 * and a per-payment credit ceiling — and the operator struck both out.
 * Safaricom posts from a changing set of addresses, so the allowlist was a
 * trap; a ceiling is a business limit, not a security control. What replaced
 * them is that SAFARICOM IS ASKED about every payment, and the credential
 * below is what lets this platform ask. That sentence is on the screen,
 * because an operator arriving here expecting the old two boxes has to be told
 * what took their place.
 *
 * Its own save, like every other section here: a section must never write
 * another section's fields. It also has to be able to send an EMPTY security
 * credential — that is how verification is switched off again, deliberately —
 * which the "skip empty values" rule the credential saves use would swallow,
 * so this block does not use it.
 *
 * The inert state is spelled out rather than left to be inferred. Without the
 * initiator identity every payment is held: the safe state, and one that looks
 * exactly like a broken integration unless the screen says which it is in.
 */
function C2bBlock({ view }: { view: BillingSettingsView['mpesa'] }) {
  const queryClient = useQueryClient();
  const [initiator, setInitiator] = useState<string | null>(null);
  const [credential, setCredential] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      if (initiator !== null) body.mpesaInitiatorName = initiator.trim();
      /*
        An EMPTIED box means "clear it", not "leave it alone".

        The lesson from the ceiling this replaces: that field skipped empty
        values while its Save button was enabled as soon as the box was
        touched, so an operator who cleared it to switch automatic crediting
        OFF got a success, a refetch, and the old value back — with automatic
        crediting still on. Sending the empty string is the only version where
        what the operator did and what the platform stored agree.
      */
      if (credential !== null) body.mpesaSecurityCredential = credential.trim();
      return api.put('/billing/settings', body);
    },
    onSuccess: async () => {
      setError(null);
      setInitiator(null);
      setCredential(null);
      await queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save'),
  });

  const inert = view.c2bVerifiable
    ? null
    : // The toggle first, because it is the only one of these the operator did
      // on purpose. Paybill money used to be asked about and auto-credited
      // with M-PESA switched off, so "off" now covers this path too and the
      // screen has to say which kind of inert it is looking at.
      !view.enabled
      ? 'M-PESA is switched off above, so paybill payments are recorded and held rather than credited.'
      : !view.initiatorName || !view.securityCredential
        ? 'No initiator credentials — Safaricom cannot be asked, so every paybill payment is held for a human.'
        : 'The M-PESA consumer key, secret and shortcode above are incomplete, so Safaricom cannot be asked.';

  return (
    <div className="mt-2 rounded-lg border border-line bg-surface-2 p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <p className="text-sm font-semibold text-ink">Paybill (C2B)</p>
        <Pill state tone={inert ? 'warn' : 'success'}>
          {inert ? 'held for review' : 'verified with Safaricom'}
        </Pill>
        <span className="text-xs text-faint">
          {inert ?? 'Every payment is checked with Safaricom before it is credited.'}
        </span>
      </div>
      <div className="mb-3 grid gap-2">
        <UrlLine label="Confirmation URL" url={view.c2bConfirmationUrl} />
        <UrlLine label="Validation URL" url={view.c2bValidationUrl} />
        <UrlLine label="Result URL" url={view.c2bResultUrl} />
        <UrlLine label="Queue timeout URL" url={view.c2bTimeoutUrl} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Initiator name (the Daraja API operator)">
          <Input
            placeholder="apitest"
            value={initiator ?? view.initiatorName}
            onChange={(e) => setInitiator(e.target.value)}
          />
        </Field>
        <Field
          label={`Initiator security credential${view.securityCredential ? ' (stored — type to replace)' : ''}`}
        >
          <Input
            type="password"
            className="font-mono text-xs"
            placeholder={view.securityCredential ? '••••••••••••••••' : 'Paste from the Daraja portal'}
            value={credential ?? ''}
            onChange={(e) => setCredential(e.target.value)}
          />
        </Field>
      </div>
      {error && <p className="pt-2 text-sm text-danger">{error}</p>}
      <div className="mt-3 flex items-start gap-3 text-xs text-faint">
        <span>
          Register the confirmation and validation URLs against the shortcode with Safaricom; the
          result URL is sent with every query and needs no registration. Paste the credential
          Daraja&apos;s own <b>Initiator Security Credential</b> tool gives you — already encrypted
          under Safaricom&apos;s certificate. It is stored encrypted, is never sent back to this
          page, and nothing in a paybill message is trusted: Safaricom&apos;s own answer is what
          credits it.
        </span>
        <Button
          size="sm"
          variant="primary"
          className="ml-auto shrink-0"
          loading={save.isPending}
          disabled={initiator === null && credential === null}
          onClick={() => save.mutate()}
        >
          Save paybill
        </Button>
      </div>
    </div>
  );
}

function UrlLine({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-faint">
      <span className="w-32 shrink-0">{label}:</span>
      <code className="rounded border border-line bg-surface px-2 py-0.5 font-mono text-[11px] text-muted">
        {url}
      </code>
      <button
        type="button"
        className="inline-flex items-center gap-1 text-primary hover:underline"
        onClick={async () => {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        <Copy className="size-3" />
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function GatewaySection({
  name,
  view,
  note,
  onToggle,
  toggling,
  onSave,
  saving,
  children,
}: {
  name: string;
  view: GatewayView;
  note: string;
  onToggle: (value: boolean) => void;
  toggling: boolean;
  onSave: () => void;
  saving: boolean;
  children: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="border-b border-line px-5 py-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <p className="text-sm font-semibold text-ink">{name}</p>
        <Pill state tone={view.configured ? 'success' : 'neutral'}>
          {view.configured ? 'configured' : 'not configured'}
        </Pill>
        <span className="text-xs text-faint">{note}</span>
        <label className="ml-auto flex cursor-pointer items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={view.enabled}
            disabled={toggling}
            onChange={(e) => onToggle(e.target.checked)}
          />
          {view.enabled ? 'Enabled' : 'Disabled'}
        </label>
      </div>
      {/* One field per row: a pasted key must be visible whole, not through a
          half-column letterbox — the fields hold any length either way, but a
          key you cannot read back looks truncated even when it is not. */}
      <div className="grid gap-4">{children}</div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-faint">
        <span>Webhook URL for the {name.split(' ')[0]} dashboard:</span>
        <code className="rounded border border-line bg-surface-2 px-2 py-0.5 font-mono text-[11px] text-muted">
          {view.webhookUrl}
        </code>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-primary hover:underline"
          onClick={async () => {
            await navigator.clipboard.writeText(view.webhookUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          <Copy className="size-3" />
          {copied ? 'Copied' : 'Copy'}
        </button>
        {/* Saving here touches ONLY this gateway's fields — whatever a
            password manager autofilled elsewhere stays out of the write. */}
        <Button size="sm" variant="primary" className="ml-auto" loading={saving} onClick={onSave}>
          Save {name.split(' ')[0]}
        </Button>
      </div>
    </div>
  );
}

/**
 * One credential input. Values stay put after saving; reveal fills them in
 * place and unmasks. Autofill is fought on every front the browser offers —
 * a Chrome that stuffed the operator's LOGIN into the M-PESA pair nearly
 * saved it over real credentials (found live), so: non-credential name,
 * autoComplete off/new-password, and the password-manager ignore hints.
 */
function CredField({
  label,
  k,
  form,
  set,
  secret,
  unmasked,
  placeholder,
}: {
  label: string;
  k: string;
  form: Record<string, string>;
  set: (k: string) => (e: { target: { value: string } }) => void;
  secret?: boolean;
  unmasked?: boolean;
  placeholder?: string;
}) {
  const [copied, setCopied] = useState(false);
  const value = form[k] ?? '';
  return (
    <div className="relative">
      <Field label={label}>
        <Input
          type={secret && !unmasked ? 'password' : 'text'}
          name={`gw-${k}`}
          value={value}
          onChange={set(k)}
          placeholder={placeholder ?? 'hidden — type to replace'}
          className="font-mono pr-16"
          autoComplete={secret ? 'new-password' : 'off'}
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
        />
      </Field>
      {value && (
        <button
          type="button"
          className="absolute bottom-2 right-2.5 inline-flex items-center gap-1 text-xs text-primary hover:underline"
          onClick={async () => {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          <Copy className="size-3" />
          {copied ? 'Copied' : 'Copy'}
        </button>
      )}
    </div>
  );
}
